import { useEffect, useState, useCallback, useMemo } from "react";
import "@/App.css";
import api from "@/lib/api";
import AuthGate from "@/components/AuthGate";
import SalesEntryForm from "@/components/SalesEntryForm";
import SalesDashboard from "@/components/SalesDashboard";
import SalesHistory from "@/components/SalesHistory";
import ExpensesManager from "@/components/ExpensesManager";
import { Toaster } from "@/components/ui/sonner";
import { Calculator, BarChart3, FileSpreadsheet, LogOut, Utensils, Loader2, Wallet } from "lucide-react";

const TABS = [
  { id: "saisie", label: "Clôture", icon: Calculator },
  { id: "dashboard", label: "Tableau de bord", icon: BarChart3 },
  { id: "depenses", label: "Dépenses", icon: Wallet },
  { id: "historique", label: "Journal", icon: FileSpreadsheet },
];

const PERIODS = [
  { id: "today", label: "Aujourd'hui" },
  { id: "7j", label: "7 jours" },
  { id: "30j", label: "30 jours" },
  { id: "month", label: "Ce mois" },
  { id: "quarter", label: "Trimestre" },
  { id: "year", label: "Année" },
  { id: "all", label: "Tout" },
  { id: "custom", label: "Personnalisé" },
];

function App() {
  const [authed, setAuthed] = useState(!!localStorage.getItem("vlf_token"));
  const [checking, setChecking] = useState(!!localStorage.getItem("vlf_token"));
  const [tab, setTab] = useState("saisie");
  const [period, setPeriod] = useState("30j");
  const todayISO = new Date().toISOString().slice(0, 10);
  const ago30ISO = new Date(Date.now() - 29 * 864e5).toISOString().slice(0, 10);
  const [customStart, setCustomStart] = useState(ago30ISO);
  const [customEnd, setCustomEnd] = useState(todayISO);
  const custom = useMemo(() => ({ start: customStart, end: customEnd }), [customStart, customEnd]);
  const [sales, setSales] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [loading, setLoading] = useState(true);

  const reload = useCallback(async () => {
    try {
      const [salesRes, expRes] = await Promise.all([api.get("/sales"), api.get("/expenses")]);
      setSales(salesRes.data);
      setExpenses(expRes.data);
    } catch (error) {
      if (process.env.NODE_ENV !== "production") console.error("Échec du chargement des données:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!authed) return;
    (async () => {
      try {
        await api.get("/auth/me");
        setChecking(false);
        reload();
      } catch {
        setChecking(false);
      }
    })();
  }, [authed, reload]);

  const logout = () => {
    localStorage.removeItem("vlf_token");
    setAuthed(false);
    setSales([]);
    setExpenses([]);
  };

  if (!authed || checking) {
    if (checking) {
      return (
        <div className="min-h-screen flex items-center justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-[#FF5A1F]" />
        </div>
      );
    }
    return (
      <div className="App">
        <AuthGate onAuth={() => { setAuthed(true); setChecking(true); }} />
        <Toaster position="top-center" theme="dark" />
      </div>
    );
  }

  return (
    <div className="App min-h-screen pb-24 sm:pb-8">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-[#0B0F12]/90 backdrop-blur-md border-b border-[#253242]">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between gap-3">
          <div className="flex items-center gap-2.5">
            <div className="w-9 h-9 rounded-lg bg-[#FF5A1F] flex items-center justify-center shrink-0">
              <Utensils className="w-5 h-5 text-white" />
            </div>
            <div className="leading-none">
              <div className="font-extrabold tracking-tight">Ventes Lekker Food</div>
              <div className="text-[11px] text-[#94A3B8] mt-0.5">Krousty Tacos • Ninove</div>
            </div>
          </div>
          <button
            data-testid="logout-button"
            onClick={logout}
            className="h-10 px-3 rounded-xl bg-[#1A222C] border border-[#253242] text-[#94A3B8] hover:text-white text-sm font-semibold flex items-center gap-2 active:scale-95 transition-all"
          >
            <LogOut className="w-4 h-4" /> <span className="hidden sm:inline">Déconnexion</span>
          </button>
        </div>

        {/* Desktop tabs */}
        <div className="hidden sm:block border-t border-[#253242]">
          <div className="max-w-6xl mx-auto px-4 flex gap-1">
            {TABS.map((t) => {
              const Icon = t.icon;
              return (
                <button
                  key={t.id}
                  data-testid={`tab-${t.id}`}
                  onClick={() => setTab(t.id)}
                  className={`px-4 py-3 text-sm font-semibold flex items-center gap-2 border-b-2 transition-colors ${
                    tab === t.id ? "border-[#FF5A1F] text-white" : "border-transparent text-[#94A3B8] hover:text-white"
                  }`}
                >
                  <Icon className="w-4 h-4" /> {t.label}
                </button>
              );
            })}
          </div>
        </div>
      </header>

      {/* Period selector (dashboard, dépenses & history) */}
      {(tab === "dashboard" || tab === "historique" || tab === "depenses") && (
        <div className="max-w-6xl mx-auto px-4 pt-4">
          <div className="flex gap-2 overflow-x-auto pb-1" data-testid="period-filter-select">
            {PERIODS.map((p) => (
              <button
                key={p.id}
                data-testid={`period-${p.id}`}
                onClick={() => setPeriod(p.id)}
                className={`shrink-0 h-9 px-3.5 rounded-full text-sm font-semibold border transition-all active:scale-95 ${
                  period === p.id
                    ? "bg-[#FF5A1F] border-[#FF5A1F] text-white"
                    : "bg-[#131920] border-[#253242] text-[#94A3B8] hover:text-white"
                }`}
              >
                {p.label}
              </button>
            ))}
          </div>
          {period === "custom" && (
            <div className="flex flex-wrap items-end gap-3 mt-3" data-testid="custom-range-picker">
              <div>
                <label className="block text-[11px] font-bold uppercase tracking-wider text-[#94A3B8] mb-1">Du</label>
                <input
                  data-testid="custom-start-input"
                  type="date"
                  value={customStart}
                  onChange={(e) => setCustomStart(e.target.value)}
                  className="h-11 px-3 rounded-xl bg-[#131920] border border-[#253242] text-white font-mono text-sm outline-none focus:border-[#FF5A1F]"
                />
              </div>
              <div>
                <label className="block text-[11px] font-bold uppercase tracking-wider text-[#94A3B8] mb-1">Au</label>
                <input
                  data-testid="custom-end-input"
                  type="date"
                  value={customEnd}
                  onChange={(e) => setCustomEnd(e.target.value)}
                  className="h-11 px-3 rounded-xl bg-[#131920] border border-[#253242] text-white font-mono text-sm outline-none focus:border-[#FF5A1F]"
                />
              </div>
            </div>
          )}
        </div>
      )}

      <main className="px-4 py-5">
        {loading ? (
          <div className="flex justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-[#FF5A1F]" />
          </div>
        ) : (
          <>
            {tab === "saisie" && <SalesEntryForm sales={sales} reload={reload} />}
            {tab === "dashboard" && <SalesDashboard sales={sales} period={period} custom={custom} expenses={expenses} />}
            {tab === "depenses" && <ExpensesManager expenses={expenses} reload={reload} period={period} custom={custom} />}
            {tab === "historique" && <SalesHistory sales={sales} reload={reload} period={period} custom={custom} />}
          </>
        )}
      </main>

      {/* Mobile bottom nav */}
      <nav className="sm:hidden fixed bottom-0 inset-x-0 z-40 bg-[#0B0F12]/95 backdrop-blur-md border-t border-[#253242]">
        <div className="flex">
          {TABS.map((t) => {
            const Icon = t.icon;
            return (
              <button
                key={t.id}
                data-testid={`mobile-tab-${t.id}`}
                onClick={() => setTab(t.id)}
                className={`flex-1 h-16 flex flex-col items-center justify-center gap-1 text-[11px] font-semibold transition-colors ${
                  tab === t.id ? "text-[#FF5A1F]" : "text-[#94A3B8]"
                }`}
              >
                <Icon className="w-5 h-5" /> {t.label}
              </button>
            );
          })}
        </div>
      </nav>

      <Toaster position="top-center" theme="dark" />
    </div>
  );
}

export default App;
